/**
 * Exit Intent Detection
 *
 * Detects when user is about to leave the page (mouse leaves viewport to top).
 * Fires only once per page view to avoid spam.
 * Useful for retargeting campaigns and understanding drop-off points.
 */

export class ExitIntentTracker {
  constructor(analytics) {
    this.analytics = analytics;
    this.hasTriggered = false;
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
  }

  /**
   * Start tracking exit intent
   */
  start() {
    if (typeof document === 'undefined' || this.hasTriggered) {
      return;
    }

    // Listen for mouse leaving document (exit intent signal)
    document.addEventListener('mouseleave', this.handleMouseLeave);
  }

  /**
   * Handle mouse leaving the document
   */
  handleMouseLeave(event) {
    // Only trigger if mouse leaves from top of viewport
    // (indicates user going to close tab or navigate away)
    if (event.clientY < 0 && !this.hasTriggered) {
      this.triggerExitIntent();
    }
  }

  /**
   * Trigger exit intent event
   */
  triggerExitIntent() {
    if (this.hasTriggered) {
      return;
    }

    this.hasTriggered = true;

    // Gather context about user's session
    const scrollPercentage = this.calculateScrollPercentage();
    const timeOnPage = this.calculateTimeOnPage();

    // Send analytics event
    if (this.analytics) {
      this.analytics.trackEvent('exit_intent.triggered', {
        page: typeof window !== 'undefined' ? window.location.pathname : '',
        scrollDepth: scrollPercentage,
        timeOnPage: timeOnPage,
      });
    }

    // Clean up listener after first trigger
    this.stop();
  }

  /**
   * Calculate how far user has scrolled
   */
  calculateScrollPercentage() {
    if (typeof window === 'undefined') {
      return 0;
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const documentHeight = document.documentElement.scrollHeight;
    const windowHeight = window.innerHeight;

    const scrollableHeight = documentHeight - windowHeight;
    if (scrollableHeight <= 0) {
      return 100;
    }

    return Math.round((scrollTop / scrollableHeight) * 100);
  }

  /**
   * Calculate approximate time spent on page
   */
  calculateTimeOnPage() {
    if (!this.startTime) {
      return 0;
    }

    return Math.round((Date.now() - this.startTime) / 1000); // seconds
  }

  /**
   * Stop tracking exit intent
   */
  stop() {
    if (typeof document === 'undefined') {
      return;
    }

    document.removeEventListener('mouseleave', this.handleMouseLeave);
  }

  /**
   * Reset for new page view
   */
  reset() {
    this.stop();
    this.hasTriggered = false;
    this.startTime = Date.now();
  }
}
